import { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronRight, Plus, X, Search } from "lucide-react";
import { supabase, BRAND, INK, TEXT, MUTED, FAINT, LINE, SURFACE, text, card, input, textareaStyle, selectStyle, btn, btnSm, chip, oneLine, linkBtn, iconBtn, errorText, savedText, segWrap, segBtn, ROW_LINE, WHITE } from "./constants";
import { BackButton, ConfirmBar, ConfirmDialog } from "./shared";
import { fetchAll } from "./constants";
import { useIsMobile } from "./lib/responsive";
import { aiFetch } from "./lib/ai";
import ClientUsers from "./ClientUsers";

// ─── MEMORIA DE CLIENTE (solo admin) ──────────────────────────────────────────
// Lo que Borja aprende de cada cliente para guiar sus siguientes procesos: qué
// perfiles le gustan y cuáles descarta, criterios que decidió y no estaban en
// el brief, cómo trabaja. Es Track aplicado al cliente.
//
// REGLA: EL CLIENTE NUNCA VE NADA DE ESTO. La fila de `clients` la lee el
// propio cliente por RLS, así que la memoria va en tablas aparte, solo admin
// (is_admin()): client_profiles (una fila por cliente) y client_people (sus
// personas, por person_id). El enlace con Track es outcomes.client_id. La
// garantía está en la base; esta pantalla, además, solo se pinta para admin.
//
// Toda escritura pide .select() y comprueba que hubo fila (RLS = 0 filas, sin
// error). Los textos se guardan al salir del campo, como Manage account.

export const CLIENT_STATUS = ["potential", "active", "past"];
const STATUS_LABEL = { potential: ["Potencial", "Potential"], active: ["Activo", "Active"], past: ["Pasado", "Past"] };
// Estado automático (sin elegir a mano, client_profiles.status = null): Activo
// si tiene alguna posición abierta, Pasado si solo tiene cerradas, Potencial
// si no tiene ninguna. Lo elegido a mano manda.
export const autoStatus = (open, any) => (open > 0 ? "active" : any > 0 ? "past" : "potential");
const ROLES = ["founder", "decision_maker", "interviewer", "other"];
const ROLE_LABEL = { founder: ["Founder", "Founder"], decision_maker: ["Decisor", "Decision maker"], interviewer: ["Entrevistador", "Interviewer"], other: ["Otro", "Other"] };

// Campos de la memoria: [columna, etiqueta ES, etiqueta EN, multilínea]
const MEMORY_FIELDS = [
  ["hidden_criteria", "Criterios escondidos", "Hidden criteria", true, "Lo que decidió y no estaba en el brief", "What they decided that wasn't in the brief"],
  ["likes", "Perfiles que le gustan", "Profiles they like", true],
  ["dislikes", "Perfiles que descarta", "Profiles they turn down", true],
  ["past_searches", "Búsquedas que hemos hecho", "Searches we have run", true, "Puesto, año y a quién se colocó. Sobre todo las anteriores a la plataforma", "Role, year and who was placed. Above all, those from before the platform"],
  ["notes", "Notas", "Notes", true],
];
const WORKSTYLE_FIELDS = [
  ["response_pace", "Ritmo de respuesta", "Response pace", false, "p. ej. contesta en 24 h", "e.g. replies within 24h"],
  ["real_decider", "Quién decide de verdad", "Who really decides", false, "p. ej. el CEO, aunque entreviste el COO", "e.g. the CEO, even if the COO interviews"],
  ["feedback_style", "Cómo quiere el feedback", "How they want feedback", false, "p. ej. llamada breve, sin informe", "e.g. short call, no report"],
  ["candidates_to_see", "Cuántos candidatos quiere ver", "How many candidates", false, "p. ej. 3 muy filtrados", "e.g. 3, tightly filtered"],
  ["preferred_channel", "Canal preferido", "Preferred channel", false, "p. ej. WhatsApp", "e.g. WhatsApp"],
];
const ALL_FIELDS = [...MEMORY_FIELDS, ...WORKSTYLE_FIELDS].map(f => f[0]);
// Detalle de cada persona del cliente (client_people): [columna, ES, EN, ayuda ES, ayuda EN]
const PERSON_FIELDS = [
  ["personality", "Cómo es", "What they're like", "Trato, carácter, lo que conviene saber antes de hablar con él", "Manner, character, what to know before talking to them"],
  ["process_values", "Qué valora del proceso", "What they value in the process", "p. ej. rapidez, pocos candidatos pero muy filtrados", "e.g. speed, few but well-filtered candidates"],
  ["notes", "Notas", "Notes", "", ""],
];


// El resumen de la IA llega en markdown sencillo: ### títulos, - viñetas y
// párrafos. Se pinta sin librería.
const SummaryMd = ({ md }) => (
  <div>
    {(md || "").split("\n").map(l => l.trim()).filter(Boolean).map((l, i) => {
      if (l.startsWith("### ")) return <div key={i} style={{ ...text.label, margin: i ? "14px 0 6px" : "0 0 6px" }}>{l.slice(4)}</div>;
      if (l.startsWith("- ")) return (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 4 }}>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: FAINT, marginTop: 9, flexShrink: 0 }} />
          <span style={text.body}>{l.slice(2).replace(/\*\*/g, "")}</span>
        </div>
      );
      return <p key={i} style={{ ...text.body, margin: "0 0 6px" }}>{l.replace(/\*\*/g, "")}</p>;
    })}
  </div>
);

const fmtDate = (d, lang) => d ? new Date(d).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ─── Vista Clients ────────────────────────────────────────────────────────────
const ClientsView = ({ lang = "en", focusClientId = null, setFocusClientId = () => {}, onCalendlySaved = () => {} }) => {
  const es = lang === "es";
  const L = (pair) => (es ? pair[0] : pair[1]);
  const isMobile = useIsMobile();
  const pad = isMobile ? 16 : 20;

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [clients, setClients] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [peopleCount, setPeopleCount] = useState({});
  const [posCount, setPosCount] = useState({});
  const [openCount, setOpenCount] = useState({});
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newStatus, setNewStatus] = useState("auto");
  const [savingNew, setSavingNew] = useState(false);
  const [newErr, setNewErr] = useState("");

  const loadList = useCallback(async () => {
    setLoading(true); setLoadErr("");
    try {
      const [cl, pr, cp, ps] = await Promise.all([
        fetchAll(() => supabase.from("clients").select("id, name, logo_url").order("id")),
        fetchAll(() => supabase.from("client_profiles").select("*").order("client_id")),
        fetchAll(() => supabase.from("client_people").select("id, client_id").order("id")),
        fetchAll(() => supabase.from("positions").select("id, client_id, status").order("id")),
      ]);
      setClients(cl.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setProfiles(Object.fromEntries(pr.map(p => [p.client_id, p])));
      const pc = {}; cp.forEach(x => { pc[x.client_id] = (pc[x.client_id] || 0) + 1; }); setPeopleCount(pc);
      const qc = {}; ps.filter(p => p.status !== "deleted").forEach(x => { qc[x.client_id] = (qc[x.client_id] || 0) + 1; }); setPosCount(qc);
      const oc = {}; ps.filter(p => p.status === "open").forEach(x => { oc[x.client_id] = (oc[x.client_id] || 0) + 1; }); setOpenCount(oc);
    } catch (e) {
      setLoadErr(e.message || String(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const createClient = async () => {
    const name = newName.trim();
    if (!name || savingNew) return;
    setSavingNew(true); setNewErr("");
    const { data: cl, error } = await supabase.from("clients").insert({ name }).select("id, name, logo_url");
    if (error || !cl?.length) { setNewErr(error?.message || (es ? "No se pudo crear." : "Couldn't create it.")); setSavingNew(false); return; }
    const { data: pr, error: e2 } = await supabase.from("client_profiles").insert({ client_id: cl[0].id, status: newStatus === "auto" ? null : newStatus }).select();
    setSavingNew(false);
    if (e2 || !pr?.length) { setNewErr(e2?.message || (es ? "El cliente se creó, pero no su memoria." : "The client was created, but not its memory.")); }
    setClients(prev => [...prev, cl[0]].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    if (pr?.length) setProfiles(prev => ({ ...prev, [cl[0].id]: pr[0] }));
    setCreating(false); setNewName("");
    setFocusClientId(cl[0].id);
  };

  if (focusClientId) {
    const client = clients.find(c => c.id === focusClientId);
    return (
      <ClientFile key={focusClientId} lang={lang} clientId={focusClientId} clientName={client?.name} onCalendlySaved={onCalendlySaved}
        profile={profiles[focusClientId] || null}
        onProfile={(p) => setProfiles(prev => ({ ...prev, [focusClientId]: p }))}
        onPeopleCount={(n) => setPeopleCount(prev => ({ ...prev, [focusClientId]: n }))}
        onBack={() => setFocusClientId(null)}
        onDeleted={() => {
          setClients(prev => prev.filter(c => c.id !== focusClientId));
          setProfiles(prev => { const n = { ...prev }; delete n[focusClientId]; return n; });
          setFocusClientId(null);
        }} />
    );
  }

  const isManual = (id) => !!profiles[id]?.status;
  const statusOf = (id) => profiles[id]?.status || autoStatus(openCount[id] || 0, posCount[id] || 0);
  const q = query.trim().toLowerCase();
  const shown = clients.filter(c => (!q || (c.name || "").toLowerCase().includes(q)) && (statusFilter === "all" || statusOf(c.id) === statusFilter));
  const cols = isMobile ? "minmax(0,1fr) auto" : "minmax(0,2fr) 110px 90px 90px 130px";

  return (
    <div style={{ padding: isMobile ? 16 : 32 }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...text.h1, fontSize: isMobile ? 22 : 26, margin: 0, lineHeight: 1.2 }}>{es ? "Clientes" : "Clients"}</h1>
          <div style={{ ...text.pageSub, marginTop: 4 }}>{es ? "Lo que sabemos de cada cliente. Solo admin: el cliente nunca ve esto." : "What we know about each client. Admin only: the client never sees this."}</div>
        </div>
        {!creating && (
          <button onClick={() => { setCreating(true); setNewErr(""); }} style={{ ...btn.primary, display: "inline-flex", alignItems: "center", gap: 6, ...(isMobile ? { width: "100%", justifyContent: "center", minHeight: 44 } : null) }}>
            <Plus size={16} strokeWidth={2} /> {es ? "Nuevo cliente" : "New client"}
          </button>
        )}
      </div>

      {creating && (
        <div style={{ ...card, padding: pad, marginBottom: 16 }}>
          <div style={{ ...text.cardTitle, marginBottom: 4 }}>{es ? "Nuevo cliente" : "New client"}</div>
          <div style={{ ...text.cardSub, marginBottom: 14 }}>{es ? "Sin cuenta en la plataforma: no se le crea usuario ni se le avisa." : "No platform account: no user is created and nobody is notified."}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") createClient(); }}
              placeholder={es ? "Nombre de la empresa" : "Company name"} style={{ ...input, flex: "1 1 220px", width: "auto", ...(isMobile ? { height: 44, fontSize: 16 } : null) }} />
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} style={{ ...selectStyle, ...(isMobile ? { height: 44, flex: "1 1 140px" } : null) }}>
              <option value="auto">{es ? "Automático" : "Automatic"}</option>
              {CLIENT_STATUS.map(s => <option key={s} value={s}>{L(STATUS_LABEL[s])}</option>)}
            </select>
            <button onClick={createClient} disabled={!newName.trim() || savingNew} style={{ ...(!newName.trim() || savingNew ? btn.disabled : btn.primary), ...(isMobile ? { flex: "1 1 120px", minHeight: 44 } : null) }}>
              {savingNew ? (es ? "Creando..." : "Creating...") : (es ? "Crear" : "Create")}
            </button>
            <button onClick={() => { setCreating(false); setNewName(""); setNewErr(""); }} style={{ ...btn.ghost, ...(isMobile ? { flex: "1 1 120px", minHeight: 44 } : null) }}>{es ? "Cancelar" : "Cancel"}</button>
          </div>
          {newErr && <div style={{ ...errorText, marginTop: 10 }}>{newErr}</div>}
        </div>
      )}

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 10, padding: `14px ${pad}px`, flexWrap: "wrap", alignItems: "center", borderBottom: `1px solid ${LINE}` }}>
          <div style={{ position: "relative", flex: "1 1 200px" }}>
            <Search size={15} strokeWidth={1.75} color={FAINT} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }} />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder={es ? "Buscar cliente" : "Search client"}
              style={{ ...input, paddingLeft: 32, ...(isMobile ? { height: 44, fontSize: 16 } : null) }} />
          </div>
          <div style={{ ...segWrap, display: "inline-flex", flexWrap: "wrap" }}>
            {["all", ...CLIENT_STATUS].map(s => {
              const on = statusFilter === s;
              return (
                <button key={s} onClick={() => setStatusFilter(s)}
                  style={{ ...segBtn(on), padding: "0 12px", height: isMobile ? 38 : 30, fontSize: 12 }}>
                  {s === "all" ? (es ? "Todos" : "All") : L(STATUS_LABEL[s])}
                </button>
              );
            })}
          </div>
        </div>

        {!isMobile && (
          <div style={{ display: "grid", gridTemplateColumns: cols, gap: 12, padding: `10px ${pad}px`, borderBottom: `1px solid ${LINE}`, ...text.label }}>
            <span>{es ? "Cliente" : "Client"}</span><span>{es ? "Estado" : "Status"}</span>
            <span>{es ? "Posiciones" : "Positions"}</span><span>{es ? "Personas" : "People"}</span><span>{es ? "Memoria" : "Memory"}</span>
          </div>
        )}

        {loading && <div style={{ padding: pad, ...text.body, color: MUTED }}>{es ? "Cargando..." : "Loading..."}</div>}
        {!loading && loadErr && (
          <div style={{ padding: pad, ...errorText, fontSize: 13 }}>
            {es ? "No se pudieron cargar los clientes: " : "Couldn't load clients: "}{loadErr}
          </div>
        )}
        {!loading && !loadErr && shown.length === 0 && (
          <div style={{ padding: pad, ...text.body, color: MUTED }}>
            {clients.length === 0 ? (es ? "Aún no hay clientes." : "No clients yet.") : (es ? "Ningún cliente coincide con la búsqueda o el filtro." : "No client matches the search or filter.")}
          </div>
        )}
        {!loading && !loadErr && shown.map((c, i) => {
          const st = statusOf(c.id);
          return (
            <button key={c.id} onClick={() => setFocusClientId(c.id)}
              style={{ width: "100%", display: "grid", gridTemplateColumns: cols, gap: 12, alignItems: "center", padding: `12px ${pad}px`, minHeight: isMobile ? 52 : undefined, border: "none", borderTop: i ? `1px solid ${ROW_LINE}` : "none", background: WHITE, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
              onMouseEnter={e => { e.currentTarget.style.background = SURFACE; }} onMouseLeave={e => { e.currentTarget.style.background = WHITE; }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: INK, ...oneLine }} title={c.name}>{c.name}</div>
                {isMobile && <div style={{ ...text.meta, marginTop: 2 }}>{posCount[c.id] || 0} {es ? "posiciones" : "positions"} · {peopleCount[c.id] || 0} {es ? "personas" : "people"}</div>}
              </div>
              <span style={{ ...chip(false), justifySelf: "start" }} title={isManual(c.id) ? (es ? "Elegido a mano" : "Set by hand") : (es ? "Automático, por sus posiciones" : "Automatic, from its positions")}>{L(STATUS_LABEL[st])}{isManual(c.id) ? "" : " · auto"}</span>
              {!isMobile && <span style={{ fontSize: 13, color: TEXT }}>{posCount[c.id] || 0}</span>}
              {!isMobile && <span style={{ fontSize: 13, color: TEXT }}>{peopleCount[c.id] || 0}</span>}
              {!isMobile && <span style={{ fontSize: 12, color: profiles[c.id]?.updated_at ? TEXT : FAINT }}>{fmtDate(profiles[c.id]?.updated_at, lang)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Ficha de un cliente ──────────────────────────────────────────────────────
function ClientFile({ lang, clientId, clientName, profile, onProfile, onPeopleCount, onBack, onDeleted, onCalendlySaved = () => {} }) {
  const es = lang === "es";
  const L = (pair) => (es ? pair[0] : pair[1]);
  const isMobile = useIsMobile();
  const pad = isMobile ? 16 : 20;

  const fromProfile = (p) => Object.fromEntries(ALL_FIELDS.map(k => [k, p?.[k] || ""]));
  const [form, setForm] = useState(() => fromProfile(profile));
  const [saved, setSaved] = useState(() => fromProfile(profile));
  const [status, setStatus] = useState(profile?.status || null);
  // Enlace de Calendly del cliente (clients.calendly_link). Vivía en el menú
  // lateral; desde el 24/10 se edita aquí, con lo demás de cómo trabaja.
  const [cal, setCal] = useState({ value: "", saved: "", state: null });
  useEffect(() => {
    supabase.from("clients").select("calendly_link").eq("id", clientId).maybeSingle()
      .then(({ data }) => { const v = data?.calendly_link || ""; setCal({ value: v, saved: v, state: null }); });
  }, [clientId]);
  const saveCal = async () => {
    const v = cal.value.trim();
    if (v === cal.saved) return;
    if (v && !/^https:\/\/[^\s]+$/i.test(v)) { setCal(c => ({ ...c, state: "invalid" })); return; }
    const { data, error } = await supabase.from("clients").update({ calendly_link: v || null }).eq("id", clientId).select("id");
    if (error || !data?.length) { setCal(c => ({ ...c, state: "error" })); return; }
    setCal({ value: v, saved: v, state: "saved" });
    onCalendlySaved(clientId, v);
    setTimeout(() => setCal(c => (c.state === "saved" ? { ...c, state: null } : c)), 2500);
  };
  const [updatedAt, setUpdatedAt] = useState(profile?.updated_at || null);
  const [saveState, setSaveState] = useState(null); // null | saving | saved | error

  const [people, setPeople] = useState([]);
  const [names, setNames] = useState({});
  const [positions, setPositions] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [candidatesTrack, setCandidatesTrack] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [newRole, setNewRole] = useState("founder");
  const [personErr, setPersonErr] = useState("");
  const [openPerson, setOpenPerson] = useState(null); // id de la persona desplegada
  // Resumen por IA de lo que dicen sus decisiones (generar y guardar: no se
  // regenera al abrir la ficha, solo al pulsar)
  const [summary, setSummary] = useState({ md: profile?.ai_summary || "", at: profile?.ai_summary_at || null });
  const [summarizing, setSummarizing] = useState(false);
  const [summaryErr, setSummaryErr] = useState("");
  const [confirm, setConfirm] = useState(null); // { kind: 'person' | 'outcome' | 'client', id }
  // Borrar un cliente: solo si no tiene posiciones (ni borradas), usuarios ni
  // candidatos. Con procesos colgando, un clic no debe llevárselos por delante.
  const [blockers, setBlockers] = useState(null); // { positions, users, candidates }
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  // Nombres por person_id, desde people (la identidad única)
  const loadNames = useCallback(async (ids) => {
    const missing = [...new Set(ids.filter(Boolean))];
    if (!missing.length) return {};
    const { data } = await supabase.from("people").select("id, full_name").in("id", missing);
    const map = Object.fromEntries((data || []).map(p => [p.id, p.full_name]));
    setNames(prev => ({ ...prev, ...map }));
    return map;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [{ data: pr }, { data: pp }, { data: ps }, { data: oc }, { data: ot }] = await Promise.all([
        supabase.from("client_profiles").select("*").eq("client_id", clientId).maybeSingle(),
        supabase.from("client_people").select("*").eq("client_id", clientId).order("created_at"),
        supabase.from("positions").select("id, title, status, created_at, date_closed").eq("client_id", clientId).neq("status", "deleted").order("created_at", { ascending: false }),
        supabase.from("outcomes").select("*").eq("client_id", clientId).order("occurred_on", { ascending: false }),
        // Entradas de Track sin cliente que mencionan su nombre: desde el 09/10
        // la posición va escrita al principio del texto, así se asocian a mano.
        clientName
          ? supabase.from("outcomes").select("*").is("client_id", null).ilike("note", `%${clientName}%`).order("occurred_on", { ascending: false }).limit(50)
          : Promise.resolve({ data: [] }),
      ]);
      const counts = await Promise.all([
        supabase.from("positions").select("id", { count: "exact", head: true }).eq("client_id", clientId),
        supabase.from("user_clients").select("client_id", { count: "exact", head: true }).eq("client_id", clientId),
        supabase.from("candidates").select("id", { count: "exact", head: true }).eq("client_id", clientId),
      ]);
      if (alive) setBlockers({ positions: counts[0].count || 0, users: counts[1].count || 0, candidates: counts[2].count || 0 });
      if (!alive) return;
      if (pr) { setForm(fromProfile(pr)); setSaved(fromProfile(pr)); setStatus(pr.status); setUpdatedAt(pr.updated_at); setSummary({ md: pr.ai_summary || "", at: pr.ai_summary_at || null }); onProfile(pr); }
      setPeople(pp || []);
      setPositions(ps || []);
      setOutcomes(oc || []);
      setCandidatesTrack(ot || []);
      await loadNames([...(pp || []).map(x => x.person_id), ...(oc || []).map(x => x.person_id), ...(ot || []).map(x => x.person_id)]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Guarda un campo (o el estado). Si aún no hay fila, la crea (upsert por client_id).
  const saveProfile = async (patch) => {
    setSaveState("saving");
    const { data, error } = await supabase.from("client_profiles")
      .upsert({ client_id: clientId, ...patch }, { onConflict: "client_id" }).select();
    if (error || !data?.length) { setSaveState("error"); return false; }
    setUpdatedAt(data[0].updated_at); onProfile(data[0]);
    setSaveState("saved");
    setTimeout(() => setSaveState(s => (s === "saved" ? null : s)), 2500);
    return true;
  };
  const saveField = async (k) => {
    if (form[k] === saved[k]) return;
    if (await saveProfile({ [k]: form[k].trim() || null })) setSaved(s => ({ ...s, [k]: form[k] }));
  };
  // s = null vuelve a automático
  const saveStatus = async (s) => {
    const prev = status;
    setStatus(s);
    if (!(await saveProfile({ status: s }))) setStatus(prev);
  };
  const derived = autoStatus(positions.filter(p => p.status === "open").length, positions.length);
  const shownStatus = status || derived;

  // ── Resumen por IA ──
  // Lee lo que el cliente ha DECIDIDO: vetos y validaciones del Mapping (con
  // sus motivos y comentarios), candidatos validados y descartados en el
  // Tablero y Track; más su memoria escrita. A la IA no le llega ningún nombre
  // de persona: solo puestos, empresas y descriptores anónimos.
  const summarize = async () => {
    setSummarizing(true); setSummaryErr("");
    try {
      const posIds = positions.map(p => p.id);
      const titleOf = Object.fromEntries(positions.map(p => [p.id, p.title]));
      const none = { data: [] };
      const [{ data: mc }, { data: mp }, { data: mm }, { data: cands }] = posIds.length
        ? await Promise.all([
          supabase.from("mapping_companies").select("position_id, name, status, veto_reason").in("position_id", posIds),
          supabase.from("mapping_profiles").select("position_id, role_title, company_descriptor, years_experience, background, status, veto_reason, published").in("position_id", posIds),
          supabase.from("mapping_comments").select("position_id, target_type, body").in("position_id", posIds),
          supabase.from("candidates").select("position_id, company, current_position, status, verdict, strengths, weaknesses").in("position_id", posIds),
        ])
        : [none, none, none, none];
      const lines = [];
      const memo = MEMORY_FIELDS.concat(WORKSTYLE_FIELDS).filter(fl => (form[fl[0]] || "").trim()).map(fl => `- ${fl[1]}: ${form[fl[0]].trim()}`);
      if (memo.length) lines.push("MEMORIA ESCRITA POR EL RECRUITER:", ...memo, "");
      if (positions.length) lines.push("POSICIONES: " + positions.map(p => `${p.title} (${p.status === "closed" ? "cerrada" : "abierta"})`).join("; "), "");
      const st = { validated: "VALIDADA", vetoed: "VETADA", proposed: "sin decidir" };
      if ((mc || []).length) lines.push("MAPPING · EMPRESAS OBJETIVO:", ...(mc || []).map(c => `- [${titleOf[c.position_id] || ""}] ${c.name}: ${st[c.status] || c.status}${c.veto_reason ? " · motivo: " + c.veto_reason : ""}`), "");
      const pubProfiles = (mp || []).filter(p => p.published);
      if (pubProfiles.length) lines.push("MAPPING · PERFILES ANÓNIMOS:", ...pubProfiles.map(p => `- [${titleOf[p.position_id] || ""}] ${p.role_title || ""}${p.company_descriptor ? ", " + p.company_descriptor : ""}${p.years_experience ? ", " + p.years_experience + " años" : ""}: ${st[p.status] || p.status}${p.veto_reason ? " · motivo: " + p.veto_reason : ""}`), "");
      if ((mm || []).length) lines.push("MAPPING · COMENTARIOS:", ...(mm || []).map(c => `- [${titleOf[c.position_id] || ""}] ${c.body}`), "");
      const decided = (cands || []).filter(c => ["validated", "interview", "references", "rejected"].includes(c.status));
      const cs = { validated: "VALIDADO", interview: "en entrevistas", references: "en referencias", rejected: "DESCARTADO" };
      if (decided.length) lines.push("TABLERO · CANDIDATOS DECIDIDOS (sin nombres):", ...decided.map(c => `- [${titleOf[c.position_id] || ""}] ${c.current_position || "?"} en ${c.company || "?"}: ${cs[c.status]}${c.verdict && c.verdict !== "—" ? " · veredicto: " + c.verdict : ""}${(c.strengths || []).length ? " · fortalezas: " + c.strengths.slice(0, 3).join(", ") : ""}${(c.weaknesses || []).length ? " · dudas: " + c.weaknesses.slice(0, 3).join(", ") : ""}`), "");
      if (outcomes.length) lines.push("TRACK (lo que pasó con personas de sus procesos):", ...outcomes.map(o => `- ${o.kind === "result" ? "Resultado" : "Juicio"}: ${(o.note || "").replace(/\s+/g, " ").slice(0, 400)}`), "");
      const evidence = (mc || []).length + pubProfiles.length + (mm || []).length + decided.length + outcomes.length;
      if (!evidence && !memo.length) { setSummaryErr(es ? "Aún no hay decisiones de este cliente que resumir: ni Mapping, ni candidatos decididos, ni Track." : "No decisions from this client to summarise yet: no Mapping, no decided candidates, no Track."); setSummarizing(false); return; }

      const res = await aiFetch({ body: JSON.stringify({
        feature: "client_summary",
        max_tokens: 1200,
        system: "Eres el analista de un headhunter de executive search. Resumes qué quiere de verdad un cliente a partir de sus DECISIONES (vetos, validaciones, descartes, comentarios y lo que pasó después), no de lo que dijo en el brief. Escribe en español, directo, sin adornos. Usa SOLO la evidencia que se te da: si algo no está, no lo inventes, y si la evidencia es escasa dilo. Cuantifica cuando puedas («vetó 4 de 6 empresas de consultoría»). Nunca nombres a personas. Formato: markdown con estos cuatro encabezados, escritos tal cual y sin comillas, cada uno seguido de 2-5 viñetas de una línea que empiezan por guion: ### Lo que valida / ### Lo que descarta y por qué / ### Criterios que no estaban en el brief / ### Para la próxima búsqueda. Nada antes del primero ni después del último.",
        messages: [{ role: "user", content: `Cliente: ${clientName || "?"}\n\n${lines.join("\n")}` }],
      }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || data?.error || res.status);
      const md = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("\n").trim();
      if (!md) throw new Error(es ? "Respuesta vacía" : "Empty response");
      const at = new Date().toISOString();
      setSummary({ md, at });
      if (!(await saveProfile({ ai_summary: md, ai_summary_at: at }))) setSummaryErr(es ? "El resumen se generó pero no se pudo guardar." : "The summary was generated but couldn't be saved.");
    } catch (e) {
      setSummaryErr((es ? "No se pudo generar el resumen: " : "Couldn't generate the summary: ") + (e.message || e));
    }
    setSummarizing(false);
  };

  // ── Personas ──
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let alive = true;
    const tmo = setTimeout(async () => {
      const { data } = await supabase.from("people").select("id, full_name, city").ilike("full_name", `%${q}%`).order("full_name").limit(8);
      if (alive) setResults(data || []);
    }, 250);
    return () => { alive = false; clearTimeout(tmo); };
  }, [search]);

  const linkPerson = async (p) => {
    setPersonErr("");
    if (people.some(x => x.person_id === p.id)) { setPersonErr(es ? "Esa persona ya está enlazada." : "That person is already linked."); return; }
    const { data, error } = await supabase.from("client_people").insert({ client_id: clientId, person_id: p.id, role: newRole }).select();
    if (error || !data?.length) { setPersonErr(error?.message || (es ? "No se pudo enlazar." : "Couldn't link.")); return; }
    setNames(prev => ({ ...prev, [p.id]: p.full_name }));
    const next = [...people, data[0]];
    setPeople(next); onPeopleCount(next.length);
    setSearch(""); setResults([]);
  };
  const updatePerson = async (id, patch) => {
    const { data, error } = await supabase.from("client_people").update(patch).eq("id", id).select();
    if (error || !data?.length) { setPersonErr(es ? "No se pudo guardar." : "Couldn't save."); return; }
    setPeople(prev => prev.map(x => (x.id === id ? data[0] : x)));
  };
  const unlinkPerson = async (id) => {
    setConfirm(null);
    const { data, error } = await supabase.from("client_people").delete().eq("id", id).select("id");
    if (error || !data?.length) { setPersonErr(es ? "No se pudo quitar." : "Couldn't remove."); return; }
    const next = people.filter(x => x.id !== id);
    setPeople(next); onPeopleCount(next.length);
  };

  // ── Borrar el cliente ──
  // Se van (en cascada) su memoria y sus personas enlazadas; las personas
  // siguen en la base y sus entradas de Track se quedan, sin cliente.
  const deleteClient = async () => {
    setDeleting(true); setDeleteErr("");
    const { data, error } = await supabase.from("clients").delete().eq("id", clientId).select("id");
    setDeleting(false);
    if (error || !data?.length) { setDeleteErr(error?.message || (es ? "No se pudo borrar." : "Couldn't delete it.")); return; }
    onDeleted();
  };

  // ── Track ──
  const setOutcomeClient = async (o, toClient) => {
    const { data, error } = await supabase.from("outcomes").update({ client_id: toClient ? clientId : null }).eq("id", o.id).select();
    setConfirm(null);
    if (error || !data?.length) return;
    if (toClient) { setOutcomes(prev => [data[0], ...prev]); setCandidatesTrack(prev => prev.filter(x => x.id !== o.id)); }
    else { setOutcomes(prev => prev.filter(x => x.id !== o.id)); setCandidatesTrack(prev => [data[0], ...prev]); }
  };

  // ── Piezas ──
  const head = (title, right) => (
    <div style={{ padding: `16px ${pad}px 12px`, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
      <span style={text.cardTitle}>{title}</span>{right}
    </div>
  );
  const row = (label, field, hint) => (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(150px, 30%) 1fr", gap: isMobile ? 6 : 16, padding: isMobile ? "12px 16px" : "10px 20px", borderTop: `1px solid ${ROW_LINE}`, alignItems: "start" }}>
      <span style={{ fontSize: 12, color: TEXT, paddingTop: isMobile ? 0 : 9 }}>{label}</span>
      <div style={{ minWidth: 0 }}>{field}{hint && <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>{hint}</div>}</div>
    </div>
  );
  const onFocus = e => { e.target.style.borderColor = BRAND; };
  const offFocus = e => { e.target.style.borderColor = LINE; };
  const fieldEl = ([k, , , multi, phEs, phEn]) => {
    const common = {
      value: form[k], onChange: e => { const v = e.target.value; setForm(f => ({ ...f, [k]: v })); if (saveState === "error") setSaveState(null); },
      onFocus, onBlur: e => { offFocus(e); saveField(k); }, placeholder: es ? phEs || "" : phEn || "",
    };
    return multi
      ? <textarea {...common} rows={3} style={{ ...textareaStyle, ...(isMobile ? { fontSize: 16 } : null) }} />
      : <input {...common} onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} style={{ ...input, ...(isMobile ? { height: 44, fontSize: 16 } : null) }} />;
  };
  const stateTxt = saveState === "saving"
    ? <span style={{ fontSize: 12, color: MUTED }}>{es ? "Guardando..." : "Saving..."}</span>
    : saveState === "error"
      ? <span style={errorText}>{es ? "No se pudo guardar. Vuelve a intentarlo." : "Couldn't save. Please try again."}</span>
      : saveState === "saved"
        ? <span style={savedText}>{es ? "✓ Guardado" : "✓ Saved"}</span>
        : <span style={{ fontSize: 12, color: MUTED }}>{updatedAt ? `${es ? "Actualizada" : "Updated"} ${fmtDate(updatedAt, lang)}` : (es ? "Se guarda al salir del campo" : "Saves when you leave the field")}</span>;
  const confirmBar = (msg, label, onRun) => (
    <ConfirmBar message={msg} actionLabel={label} cancelLabel={es ? "Cancelar" : "Cancel"}
      onConfirm={onRun} onCancel={() => setConfirm(null)} style={{ marginTop: 8 }} />
  );
  const outcomeRow = (o, linked, i) => (
    <div key={o.id} style={{ padding: `12px ${pad}px`, borderTop: i ? `1px solid ${ROW_LINE}` : "none" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: INK }}>{names[o.person_id] || (es ? "Persona" : "Person")}</span>
        <span style={chip(false)}>{o.kind === "result" ? (es ? "Resultado" : "Result") : (es ? "Juicio" : "Judgement")}</span>
        <span style={text.meta}>{fmtDate(o.occurred_on || o.created_at, lang)}</span>
        <span style={{ marginLeft: "auto" }}>
          {linked
            ? <button onClick={() => setConfirm({ kind: "outcome", id: o.id })} style={{ ...linkBtn, color: MUTED, fontWeight: 600 }}>{es ? "Quitar" : "Remove"}</button>
            : <button onClick={() => setOutcomeClient(o, true)} style={linkBtn}>{es ? "Asociar" : "Link"}</button>}
        </span>
      </div>
      <div style={{ ...text.body, whiteSpace: "pre-wrap", marginTop: 4 }}>{o.note}</div>
      {linked && confirm?.kind === "outcome" && confirm.id === o.id && confirmBar(
        es ? `¿Quitar esta entrada de ${clientName}? Sigue en el Track de la persona.` : `Remove this entry from ${clientName}? It stays in the person's Track.`,
        es ? "Quitar" : "Remove", () => setOutcomeClient(o, false))}
    </div>
  );

  // Borrar cliente: al final y en gris; si hay procesos colgando, se dice por qué no
  const deleteBlock = () => {
    if (!blockers) return null;
    const why = [
      blockers.positions && (es ? `${blockers.positions} posiciones` : `${blockers.positions} positions`),
      blockers.users && (es ? `${blockers.users} usuarios` : `${blockers.users} users`),
      blockers.candidates && (es ? `${blockers.candidates} candidatos` : `${blockers.candidates} candidates`),
    ].filter(Boolean);
    if (why.length) return (
      <div style={{ padding: "4px 2px", ...text.meta, fontWeight: 400, lineHeight: 1.5 }}>
        {es ? `No se puede borrar: tiene ${why.join(", ")} en la plataforma.` : `Can't be deleted: it has ${why.join(", ")} on the platform.`}
      </div>
    );
    // Borrar: confirmación en ventana flotante, como todo botón rojo (24/10)
    return (
      <div style={{ padding: "4px 2px" }}>
        <button onClick={() => setConfirm({ kind: "client", id: clientId })}
          style={{ ...linkBtn, color: FAINT, ...(isMobile ? { minHeight: 44 } : null) }}>
          {es ? "Borrar cliente" : "Delete client"}
        </button>
        {confirm?.kind === "client" && (
          <ConfirmDialog title={es ? `¿Borrar ${clientName}?` : `Delete ${clientName}?`}
            message={es
              ? "Se borran su memoria y la lista de sus personas. Las personas siguen en la base y sus entradas de Track se quedan, sin cliente. No se puede deshacer."
              : "Its memory and its list of people are deleted. The people stay in the database and their Track entries remain, without a client. This can't be undone."}
            actionLabel={deleting ? (es ? "Borrando..." : "Deleting...") : (es ? "Borrar cliente" : "Delete client")}
            cancelLabel={es ? "Cancelar" : "Cancel"} busy={deleting}
            onConfirm={deleteClient} onCancel={() => { setConfirm(null); setDeleteErr(""); }}>
            {deleteErr && <div style={errorText}>{deleteErr}</div>}
          </ConfirmDialog>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? 16 : 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 12, marginBottom: 6, flexWrap: "wrap" }}>
        <BackButton onClick={onBack} ariaLabel={es ? "Volver" : "Back"} />
        <h1 style={{ ...text.h1, fontSize: isMobile ? 22 : 22, margin: 0, flex: isMobile ? 1 : undefined, minWidth: 0, overflowWrap: "anywhere" }}>{clientName || (es ? "Cliente" : "Client")}</h1>
        <div style={{ ...segWrap, display: "inline-flex", ...(isMobile ? { width: "100%" } : { marginLeft: "auto" }) }}>
          {CLIENT_STATUS.map(s => {
            const on = shownStatus === s;
            return (
              <button key={s} onClick={() => saveStatus(s)}
                style={{ ...segBtn(on), padding: "0 14px", height: isMobile ? 40 : 32, flex: isMobile ? 1 : undefined, fontSize: 12 }}>
                {L(STATUS_LABEL[s])}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ ...text.pageSub, marginBottom: 20, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
        <span>{es ? "Solo admin. El cliente nunca ve esta ficha." : "Admin only. The client never sees this file."}</span>
        <span>
          {status
            ? <>{es ? "Estado elegido a mano." : "Status set by hand."} <button onClick={() => saveStatus(null)} style={linkBtn}>{es ? "Volver a automático" : "Back to automatic"}</button></>
            : (es ? "Estado automático: Activo si tiene alguna posición abierta, Pasado si solo cerradas, Potencial si ninguna. Pulsa otro para fijarlo." : "Automatic status: Active with an open position, Past with only closed ones, Potential with none. Pick one to set it.")}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) 340px", gap: 16, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            {head(es ? "Lo que dicen sus decisiones" : "What their decisions say",
              <button onClick={summarize} disabled={summarizing || loading}
                style={{ ...(summarizing || loading ? btn.disabled : (summary.md ? btn.ghost : btn.primary)), ...btnSm, ...(isMobile ? { minHeight: 40 } : null) }}>
                {summarizing ? (es ? "Resumiendo..." : "Summarising...") : summary.md ? (es ? "Regenerar" : "Regenerate") : (es ? "✦ Resumir con IA" : "✦ Summarise with AI")}
              </button>)}
            <div style={{ padding: `0 ${pad}px ${pad}px` }}>
              {summary.md
                ? <>
                    <SummaryMd md={summary.md} />
                    <div style={{ ...text.meta, marginTop: 10 }}>{es ? "Generado" : "Generated"} {fmtDate(summary.at, lang)} · {es ? "a partir del Mapping, el Tablero, Track y la memoria. Revísalo: es un borrador, no un hecho." : "from Mapping, the Board, Track and the memory. Check it: a draft, not a fact."}</div>
                  </>
                : <div style={{ ...text.body, color: MUTED }}>{es ? "Lee sus vetos y validaciones del Mapping, los candidatos que validó o descartó y su Track, y resume qué quiere de verdad. Se guarda; solo se rehace al pulsar." : "Reads their Mapping vetoes and validations, the candidates they validated or turned down and their Track, and sums up what they really want. It's saved; it's only redone when you press."}</div>}
              {summaryErr && <div style={{ ...errorText, marginTop: 10 }}>{summaryErr}</div>}
            </div>
          </div>

          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            {head(es ? "Memoria" : "Memory", stateTxt)}
            {MEMORY_FIELDS.map(f => <div key={f[0]}>{row(es ? f[1] : f[2], fieldEl(f))}</div>)}
          </div>
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            {head(es ? "Forma de trabajar" : "How they work")}
            {WORKSTYLE_FIELDS.map(f => <div key={f[0]}>{row(es ? f[1] : f[2], fieldEl(f))}</div>)}
            {row(es ? "Enlace de Calendly" : "Calendly link", (
              <input value={cal.value} placeholder="https://calendly.com/..."
                onChange={e => { const v = e.target.value; setCal(c => ({ ...c, value: v, state: null })); }}
                onFocus={onFocus} onBlur={e => { offFocus(e); saveCal(); }} onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                style={{ ...input, ...(isMobile ? { height: 44, fontSize: 16 } : null) }} />
            ), cal.state === "saved" ? <span style={savedText}>{es ? "✓ Guardado" : "✓ Saved"}</span>
              : cal.state === "invalid" ? <span style={errorText}>{es ? "Tiene que ser un enlace https://" : "It must be an https:// link"}</span>
              : cal.state === "error" ? <span style={errorText}>{es ? "No se pudo guardar." : "Couldn't save."}</span>
              : (es ? "Para agendar entrevistas con sus candidatos." : "Used to schedule interviews with their candidates."))}
          </div>
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            {head(`Track (${outcomes.length})`, <span style={{ fontSize: 12, color: MUTED }}>{es ? "Lo que pasó con las personas de sus procesos" : "What happened with people from their searches"}</span>)}
            {loading && <div style={{ padding: `0 ${pad}px ${pad}px`, ...text.body, color: MUTED }}>{es ? "Cargando..." : "Loading..."}</div>}
            {!loading && outcomes.length === 0 && (
              <div style={{ padding: `0 ${pad}px ${pad}px`, ...text.body, color: MUTED }}>{es ? "Ninguna entrada de Track asociada a este cliente." : "No Track entries linked to this client."}</div>
            )}
            {outcomes.map((o, i) => outcomeRow(o, true, i))}
            {candidatesTrack.length > 0 && (
              <>
                <div style={{ padding: `8px ${pad}px`, background: SURFACE, borderTop: `1px solid ${LINE}`, ...text.label }}>
                  {es ? `Sin cliente que mencionan «${clientName}» (${candidatesTrack.length})` : `Unlinked entries mentioning “${clientName}” (${candidatesTrack.length})`}
                </div>
                {candidatesTrack.map((o, i) => outcomeRow(o, false, i))}
              </>
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <ClientUsers lang={lang} clientId={clientId} clientName={clientName} />
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            {head(`${es ? "Personas" : "People"} (${people.length})`)}
            {people.length === 0 && !loading && <div style={{ padding: `0 ${pad}px 12px`, ...text.body, color: MUTED }}>{es ? "Aún no hay nadie enlazado." : "Nobody linked yet."}</div>}
            {people.map(p => (
              <div key={p.id} style={{ padding: `12px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: INK, flex: 1, ...oneLine }} title={names[p.person_id] || ""}>{names[p.person_id] || (es ? "Persona" : "Person")}</span>
                  <select value={p.role} onChange={e => updatePerson(p.id, { role: e.target.value })} aria-label={es ? "Rol" : "Role"}
                    style={{ ...selectStyle, height: isMobile ? 40 : 30, fontSize: isMobile ? 16 : 12 }}>
                    {ROLES.map(r => <option key={r} value={r}>{L(ROLE_LABEL[r])}</option>)}
                  </select>
                  <button onClick={() => setConfirm({ kind: "person", id: p.id })} aria-label={es ? "Quitar" : "Remove"}
                    style={{ ...iconBtn, color: FAINT, ...(isMobile ? { width: 40, height: 40 } : null) }}>
                    <X size={16} strokeWidth={1.75} />
                  </button>
                </div>
                {/* Lo personal: cómo es, qué valora del proceso y notas. Se
                    guarda al salir de cada campo, como la memoria. */}
                <button type="button" onClick={() => setOpenPerson(openPerson === p.id ? null : p.id)} aria-expanded={openPerson === p.id}
                  style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", textAlign: "left", width: "100%", ...(isMobile ? { minHeight: 40 } : null) }}>
                  {openPerson === p.id ? <ChevronDown size={14} strokeWidth={1.75} color={MUTED} /> : <ChevronRight size={14} strokeWidth={1.75} color={MUTED} />}
                  <span style={{ fontSize: 12, color: (p.personality || p.process_values || p.notes) ? TEXT : FAINT, ...oneLine }}>
                    {openPerson === p.id
                      ? (es ? "Ocultar detalle" : "Hide details")
                      : (p.personality || p.process_values || p.notes || (es ? "Cómo es, qué valora, notas" : "What they're like, what they value, notes"))}
                  </span>
                </button>
                {openPerson === p.id && PERSON_FIELDS.map(([k, labEs, labEn, phEs, phEn]) => (
                  <div key={k} style={{ marginTop: 10 }}>
                    <div style={{ ...text.label, marginBottom: 4 }}>{es ? labEs : labEn}</div>
                    <textarea defaultValue={p[k] || ""} rows={3} placeholder={es ? phEs : phEn}
                      onFocus={onFocus}
                      onBlur={e => { offFocus(e); const v = e.target.value.trim(); if (v !== (p[k] || "")) updatePerson(p.id, { [k]: v || null }); }}
                      style={{ ...textareaStyle, fontSize: isMobile ? 16 : 12 }} />
                  </div>
                ))}
                {confirm?.kind === "person" && confirm.id === p.id && confirmBar(
                  es ? `¿Quitar a ${names[p.person_id] || "esta persona"} de ${clientName}? Su ficha y su Track no se tocan.` : `Remove ${names[p.person_id] || "this person"} from ${clientName}? Their profile and Track stay.`,
                  es ? "Quitar" : "Remove", () => unlinkPerson(p.id))}
              </div>
            ))}
            <div style={{ padding: `12px ${pad}px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
              <div style={{ ...text.label, marginBottom: 8 }}>{es ? "Enlazar a alguien" : "Link someone"}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={search} onChange={e => { setSearch(e.target.value); setPersonErr(""); }} placeholder={es ? "Buscar por nombre" : "Search by name"}
                  onFocus={onFocus} onBlur={offFocus} style={{ ...input, flex: 1, width: "auto", ...(isMobile ? { height: 44, fontSize: 16 } : null) }} />
                <select value={newRole} onChange={e => setNewRole(e.target.value)} aria-label={es ? "Rol" : "Role"} style={{ ...selectStyle, ...(isMobile ? { height: 44 } : null) }}>
                  {ROLES.map(r => <option key={r} value={r}>{L(ROLE_LABEL[r])}</option>)}
                </select>
              </div>
              {search.trim().length >= 2 && results.length === 0 && (
                <div style={{ ...text.meta, marginTop: 8 }}>{es ? "Nadie con ese nombre en la base." : "Nobody with that name in the database."}</div>
              )}
              {results.map(r => (
                <button key={r.id} onClick={() => linkPerson(r)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, marginTop: 6, padding: "8px 10px", minHeight: isMobile ? 44 : undefined, border: `1.5px solid ${LINE}`, borderRadius: 8, background: WHITE, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: INK, flex: 1, ...oneLine }}>{r.full_name}</span>
                  {r.city && <span style={text.meta}>{r.city}</span>}
                  <span style={{ fontSize: 12, fontWeight: 700, color: BRAND }}>{es ? "Enlazar" : "Link"}</span>
                </button>
              ))}
              {personErr && <div style={{ ...errorText, marginTop: 8 }}>{personErr}</div>}
            </div>
          </div>

          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            {head(`${es ? "Posiciones" : "Positions"} (${positions.length})`)}
            {positions.length === 0 && !loading && <div style={{ padding: `0 ${pad}px ${pad}px`, ...text.body, color: MUTED }}>{es ? "Ninguna posición en la plataforma." : "No positions on the platform."}</div>}
            {positions.map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: `10px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: INK, flex: 1, ...oneLine }} title={p.title}>{p.title}</span>
                <span style={chip(false)}>{p.status === "closed" ? (es ? "Cerrada" : "Closed") : (es ? "Abierta" : "Open")}</span>
                <span style={text.meta}>{fmtDate(p.created_at, lang)}</span>
              </div>
            ))}
          </div>

          {deleteBlock()}
        </div>
      </div>
    </div>
  );
}

export default ClientsView;
